import { AnimatePresence } from "framer-motion";
import { noop } from "lodash";

import { ClickableElement } from "@/components/ClickableElement";
import { DataMode, DataModeType } from "@/components/flow/shared/Deploy/documentation/types";
import { useDeployContext } from "@/contexts/DeployContext";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import { useFlow } from "@/contexts/FlowContext";
import { useCurrentMedia, useCurrentWorkspace, useWorkflowToken } from "@/store";
import { QRCodeDisplay } from "@/components/flow/shared/QRCode/QRCodeDisplay";
import { ShareButton } from "@/components/flow/deploy/ShareButton";
import { useVisibilityThroughWorkspace } from "@/hooks/useVisibilityThroughWorkspace";
import { useMedia } from "@/contexts/MediaContext";
import { useModel } from "@/contexts/ModelContext";
import { InferenceStatus, useInferenceQueueItem } from "@/store";
import { useDownloadAnnotatedFile } from "@/hooks/useDownloadAnnotatedFile";
import { useMemo } from "react";
import { useAsyncBoundOperation } from "@/hooks/useApi";
import { ErrorInfo } from "@/components/ErrorInfo";

const DataSelector = ({
    icon,
    title,
    onClick,
    isSelected,
    disabled
}: {
    icon: string;
    title: string;
    onClick: () => void | Promise<void> | Promise<void | null>;
    isSelected: boolean;
    disabled?: boolean;
}) => {
    return (
        <ClickableElement
            icon={icon}
            title={title}
            onClick={onClick}
            isSelected={isSelected}
            disabled={disabled}
            hoverClassName="hover:bg-white hover:border-brand"
            iconClassName="text-brand"
            titleHoverClassName="group-hover:text-purboflow-600"
            descriptionHoverClassName="group-hover:text-purboflow-600"
        />
    );
};

export const IntegrateSideMenu = () => {
    const { task } = useFlow();
    const { dataMode, setDataMode, workflow } = useDeployContext();
    const { track } = useAnalytics();
    const { isTestingNewFiles, isDownloading } = useModel();
    const currentMedia = useCurrentMedia();

    const inferenceQueueItem = useInferenceQueueItem(currentMedia?.file.id);

    const visibility = useVisibilityThroughWorkspace();

    // Use shared download hook
    const doDownload = useDownloadAnnotatedFile({
        watermarkUrl: "https://rapid.roboflow.com",
        task
    });

    const handleDataModeChange = (mode: DataModeType) => {
        console.log("Data mode change requested:", mode);
        setDataMode(mode);
        track({ event: AnalyticsFlowEvent.DATA_MODE_SELECTED, data: { task, dataMode: mode } });
    };

    const {
        isLoading: isLoadingDownload,
        error: errorDownload,
        reset: resetDownload,
        invoke: invokeDownload
    } = useAsyncBoundOperation(doDownload);

    const downloadDisabled = useMemo(() => {
        if (isDownloading || isLoadingDownload || isTestingNewFiles) return true;
        if (inferenceQueueItem?.status !== InferenceStatus.COMPLETED) return true;
        return false;
    }, [isDownloading, isLoadingDownload, isTestingNewFiles, inferenceQueueItem]);

    return (
        <AnimatePresence mode="wait">
            <div className="content flex flex-col justify-between">
                <div className="flex flex-col gap-2">
                    <DataSelector
                        icon="far fa-code"
                        title="Integrate My Model"
                        onClick={() => handleDataModeChange(DataMode.IMAGE)}
                        isSelected={dataMode === DataMode.IMAGE}
                    />
                    <DataSelector
                        icon="far fa-upload"
                        title="Test on More Files"
                        onClick={() => handleDataModeChange(DataMode.TEST_FILES)}
                        isSelected={dataMode === DataMode.TEST_FILES}
                    />

                    <div className="my-2 flex items-center gap-2 text-xs text-tertiary">
                        <div className="h-px w-full bg-gray-200"></div>
                        OR
                        <div className="h-px w-full bg-gray-200"></div>
                    </div>
                    {/*  <DataSelector
                                icon="far fa-mobile"
                                title="Run on My Phone"
                                onClick={() => handleDataModeChange(DataMode.PHONE)}
                                isSelected={dataMode === DataMode.PHONE}
                            /> */}
                    <DataSelector
                        icon={
                            isDownloading || isLoadingDownload
                                ? "far fa-spinner fa-spin"
                                : "far fa-download"
                        }
                        title={currentMedia?.file.isVideo ? "Download Video" : "Download Image"}
                        onClick={downloadDisabled ? noop : invokeDownload}
                        isSelected={false}
                        disabled={downloadDisabled}
                    />

                    {errorDownload && (
                        <ErrorInfo error={errorDownload} onRetryClick={resetDownload} />
                    )}

                    {visibility === "public" && <ShareButton />}
                </div>
            </div>
        </AnimatePresence>
    );
};
